// 本来想着放 pinia 里的，但考虑到要移植到本体（用的 vuex），还是写在 ts 文件里方便迁移

// ￥￥\/ %1 %2 ...（参数列表，最多允许 9 个参数）\￥/(xxx,xxx,xxx（参数赋值列表）)\/￥￥
// phrase{
// id(10001 自增),
// name:string,
// desc:string,
// content:string,
// }
// 解析方法：去掉空格（空格无关解析，去掉￥￥...￥￥内的空格方便些，但**注意不要去掉参数值内的空格**），以特定内容进行字符串解析与提取替换
// 提供唯一内置常量（￥￥\/WEB_DOMAIN\/￥￥：当前域名，自动获取）draft、backup 根元素
// md 允许运行 js，提供获取 js 的 window 的变量的方法，参数为|var|变量名|var|
// 废置所有 dynamicPhrase 相关的更改，仅提供静态短语的相关方法，原因是几乎无用与不可控制性（变量名一旦修改就全部失效），因此仅保留 RESOURCE_BASE 这一个写死的动态短语
import type { DynamicPhrase, Phrase } from "@/types";
import type { AxiosResponse, AxiosResp, AxiosError } from "axios";
import type { SelectOption } from "naive-ui";
import api from "@/utils/api";
import { ref, render } from "vue";
import tools from "./tools";

let phraseList = ref<Phrase[]>([]); // 普通短语，不采用替换操作，以直接插入转化后的字符串的形式，如鼠标 hover 的黑幕消失效果快捷填入
// let dynamicPhraseList: DynamicPhrase[] = []; // 动态短语，需要实时替换操作，设计运行 js 代码来获取具体值，在不同的环境下可能会转化不同
let phraseTypes: SelectOption[] = [
  {
    label: "静态短语",
    value: "static",
  },
  {
    label: "动态短语",
    value: "dynamic",
  },
]; // 短语类型列表，提供给前端选择短语的下拉列表使用

// 动态短语列表，标记 UNIQUE_VAL_POST_REF_xxx 变量为写死在 md 处理函数 processMdContent 中的动态短语
const dynamicPhrase: DynamicPhrase[] = [
  {
    name: "获取资源前缀地址",
    desc: "置于 markdown 资源（如图片等）的 URL 前面，以便在不同环境下都能转换找到资源。任何使用了 MdPreview 或 MdEditor 的组件都应该显示定义一次 window.resourceBaseUrl",
    valName: "RESOURCE_BASE", // 上传图片部分的 valName已写死，不要随便修改
    valueCode: () => {
      return window.resourceBaseUrl;
    },
  },
  {
    name: "获取当前域名",
    desc: "站点的当前域名（因迁移等因素可能会改变）",
    valName: "HOST_URL", // 上传图片部分的 valName已写死，不要随便修改
    valueCode: () => {
      return window.location.host;
    },
  },
  {
    name: "获取当前服务器主机端口",
    desc: "请求服务器主机的端口",
    valName: "HOST_PORT", // 上传图片部分的 valName已写死，不要随便修改
    valueCode: () => {
      return window.location.port;
    },
  },
  {
    name: "获取当前页面完整 URL",
    desc: "如题所示",
    valName: "PAGE_HREF", // 上传图片部分的 valName已写死，不要随便修改
    valueCode: () => {
      return window.location.href;
    },
  },
];

const mdContentReg = /￥￥\\\/([^\\]+)\\￥\/\(([^()]+)\)\\\/￥￥/g;
// const mdContentReplaceReg = /(^|[^\\])%(\d+)/g;
const mdContentReplaceReg = /(^|[^\\])%(\d)/g; // 仅匹配 1-9 的数字，避免匹配到 10 以上的数字

const phraseInit = () => {
  api.request
    .get("/getphrase")
    .then((res: AxiosResponse<Phrase[]>) => {
      // const testPatternReg = new RegExp(`^${mdContentReg.source}$`); // 构造测试正则表达式，将全局匹配模式去掉
      // for (let i = 0; i < res.data.length; i++) {
      //   if (!testPatternReg.test(res.data[i].content)) {
      //     window.$notifyError(
      //       "短语内容错误",
      //       `短语 ${res.data[i].name} 的内容 ${res.data[i].content} 不符合格式要求（不太可能的报错）`
      //     );
      //     continue;
      //   }
      // }
      for (let i = 0; i < res.data.length; i++) {
        const count = countParams(res.data[i].content);
        if (count > 9 || count == 0) {
          window.$notifyError(
            "短语内容错误",
            `短语 ${res.data[i].name} 的内容 ${res.data[i].content} 参数个数不符合格式要求（不太可能的报错）`
          );
          return;
        }
      }
      phraseList.value = res.data;
    })
    .catch((err: AxiosError) => {
      const resp = err.response as AxiosResp;
      console.error("获取短语失败", resp.msg);
      window.$notifyError("获取短语失败", resp.msg);
    });
  //   api.request
  //     .get("/getdynamicphrase")
  //     .then((res: AxiosResponse<DynamicPhrase[]>) => {
  //       dynamicPhraseList = res.data;
  //     })
  //     .catch((err: AxiosError) => {
  //       const resp = err.response as AxiosResp;
  //       console.error("获取动态短语失败", resp.msg);
  //       window.$notifyError("获取动态短语失败", resp.msg);
  //     });
};

// 获取形如 "","",|var|varName|var| 的字符串的参数，获取字符串包裹的内容和动态短语 varName 名，已废弃
// 原本计划插入这些内容，然后实施动态替换后渲染，不过相比于直接插入替换占位符后内容，复杂度，实用性和不可确定性（删了怎么办？改的意义是？每次渲染前都请求一遍短语列表？）都不太好，因此废弃
const getValFromValueList: (valueListStr: string) => string[] = (
  valueListStr: string
) => {
  // 去除空格
  const cleaned: string = valueListStr.replace(
    /\s*(?=(?:[^"]*"[^"]*")*[^"]*$)/g,
    ""
  );
  //   const regex = /"([^"]*)"|\|var\|([^|]+)\|var\|/g;
  const regex = /"((?:[^"\\]|\\.)*)"|\|var\|([^|]+)\|var\|/g; // 增加了允许\"的转义的正则表达式
  const result: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(cleaned)) !== null) {
    // 去除字符串外空格
    if (match[1] !== undefined) {
      result.push(match[1]);
    } else if (match[2] !== undefined) {
      const varName: string = match[2];
      //   const value = dynamicPhraseList.find((value: DynamicPhrase) => {
      //     return value.name === varName;
      //   }); // 改为运行js代码来获取具体值，具体设计为对动态表存储名字，替换变量名和js函数代码，其值赋值为函数返回值（这样操作空间就更多了）
      //   // 什么？还想传参？这合理吗？我的建议是 phrase 就算能传参也是固定的，比如来两个动态 phrase，第一个为全局变量赋参，第二个获取这个参数然后运行，这不更灵活？
      //   if (value === undefined) {
      //     console.error(`动态变量 ${varName} 未找到`);
      //     continue;
      //   } else {
      //     result.push(String(new Function(value.code)()));
      //   }
      const value = dynamicPhrase.find((value: DynamicPhrase) => {
        return value.valName === varName;
      });
      if (value === undefined) {
        console.error(`动态变量 ${varName} 未找到`);
        continue;
      } else {
        result.push(String(value.valueCode()));
      }
    }
  }

  console.log(result);
  return result;
};

// 获取静态 phrase 添加位参数后的内容，比如￥￥\/在这个%1是第一个占位符，%2是第二个\￥/("占位","占位")\/￥￥
// 原本计划插入这些内容，然后实施动态替换后渲染，不过相比于直接插入替换占位符后内容，复杂度，实用性和不可确定性（删了怎么办？改的意义是？每次渲染前都请求一遍短语列表？）都不太好，因此废弃
const generatePhraseContent = (
  phraseContent: string,
  params: string[]
): string => {
  // 处理短语 reg，负责提取出形如 ￥￥\/ %1 %2 ...（参数列表，最多允许 9 个参数）\￥/(xxx,xxx,xxx（参数赋值列表）)\/￥￥ 的替换字符串和参数列表字符串
  // 替换原本的 mdContent 的字符串参数列表的 reg，其对应 %1 %2 ... %9 形式将会被替换为提供数组对应下标的值，如 %1 替换为 arr[0]，%2 替换为 arr[1]，以此类推
  // 但注意，\%1 形式的字符串不会被替换（提供的转义功能）
  return phraseContent.replaceAll(
    mdContentReplaceReg,
    (match: string, prefix: string, indexStr: string) => {
      const index = parseInt(indexStr, 10) - 1; // %1 对应 valList[0]
      const replacement = params[index] ? params[index] : `%${indexStr}`;
      return prefix + replacement;
    }
  );
};

// 将形如 ￥￥\/ %1 %2 ...（参数列表，最多允许 9 个参数）\￥/xxx,xxx,xxx（参数赋值列表）\/￥￥ 转换后的字符串（占位符被参数替换后的字符串）
// 原本计划插入这些内容，然后实施动态替换后渲染，不过相比于直接插入替换占位符后内容，复杂度，实用性和不可确定性（删了怎么办？改的意义是？每次渲染前都请求一遍短语列表？）都不太好，因此废弃
// const processMdContent = (mdContent: string): string => {
//   // 处理短语 reg，负责提取出形如 ￥￥\/ %1 %2 ...（参数列表，最多允许 9 个参数）\￥/(xxx,xxx,xxx（参数赋值列表）)\/￥￥ 的替换字符串和参数列表字符串
//   // 替换原本的 mdContent 的字符串参数列表的 reg，其对应 %1 %2 ... %9 形式将会被替换为提供数组对应下标的值，如 %1 替换为 arr[0]，%2 替换为 arr[1]，以此类推
//   // 但注意，\%1 形式的字符串不会被替换（提供的转义功能）
//   let afterStaticProcessMdContent = mdContent.replaceAll(
//     mdContentReg,
//     (match: string, originalContent: string, valListStr: string) => {
//       const valList = getValFromValueList(valListStr);
//       return generatePhraseContent(originalContent, valList);
//     }
//   );
//   // 处理动态短语
//   for (let i = 0; i < dynamicPhrase.length; i++) {
//     afterStaticProcessMdContent = afterStaticProcessMdContent.replaceAll(
//       `￥￥\\\/${dynamicPhrase[i].valName}\\\/￥￥`,
//       dynamicPhrase[i].valueCode()
//     );
//   }
//   console.log(
//     "==================== after process",
//     afterStaticProcessMdContent
//   );
//   return afterStaticProcessMdContent;
// };

// 新版的 processMdContent，仅替换动态短语内容
const processMdContent = (
  mdContent: string,
  ManualReplace: Record<string, string> | undefined = undefined
): string => {
  // 处理短语 reg，负责提取出形如 ￥￥\/ %1 %2 ...（参数列表，最多允许 9 个参数）\￥/(xxx,xxx,xxx（参数赋值列表）)\/￥￥ 的替换字符串和参数列表字符串
  // 替换原本的 mdContent 的字符串参数列表的 reg，其对应 %1 %2 ... %9 形式将会被替换为提供数组对应下标的值，如 %1 替换为 arr[0]，%2 替换为 arr[1]，以此类推
  // 但注意，\%1 形式的字符串不会被替换（提供的转义功能）
  // 处理动态短语
  for (let i = 0; i < dynamicPhrase.length; i++) {
    const replaceStr =
      ManualReplace && ManualReplace[dynamicPhrase[i].valName]
        ? ManualReplace[dynamicPhrase[i].valName]
        : dynamicPhrase[i].valueCode();
    mdContent = mdContent.replaceAll(
      `￥￥\\\/${dynamicPhrase[i].valName}\\\/￥￥`,
      replaceStr
    );
  }
  // 处理文章引用
  mdContent = mdContent.replaceAll(
    /￥￥\\\/UNIQUE_VAL_POST_REF_(\d+)\\\/￥￥/g,
    (match: string, postIDStr: string) => {
      const postID = parseInt(postIDStr);
      const post = window.PostRefUsePostList.find((p) => p.id === postID);
      if (post) {
        post.is_delete = false;
        post.is_has_draft = false;
        const postRefBox = tools.generatePostRefBox(post);
        return postRefBox;
      } else {
        console.error(`文章引用 ${postID} 未找到`);
        return match;
      }
    }
  );
  return mdContent;
};

// 提供的短语内容和参数列表，返回形如 ￥￥\/ %1 %2 ...（参数列表，最多允许 9 个参数）\￥/(xxx,xxx,xxx（参数赋值列表）)\/￥￥ 的字符串
// 原本计划插入这些内容，然后实施动态替换后渲染，不过相比于直接插入替换占位符后内容，复杂度，实用性和不可确定性（删了怎么办？改的意义是？每次渲染前都请求一遍短语列表？）都不太好，因此废弃
const phraseContentToPhraseWithParams = (
  phraseContent: string,
  stringParams: string[]
): string => {
  const paramStr = stringParams
    .map((param) => {
      const dynamic = dynamicPhrase.find((d) => d.valName === param);
      return dynamic ? `|var|${param}|var|` : `"${param}"`;
    })
    .join(",");
  return `￥￥\\\/${phraseContent}\\￥\/(${paramStr})\\\/￥￥`;
};

// 统计短语内容中的占位符个数，最多允许 8 个占位符（%1 %2 ... %9）
const countParams = (content: string): number => {
  let count = 0;
  const regex = new RegExp(
    mdContentReplaceReg.source,
    mdContentReplaceReg.flags
  );
  let match: RegExpExecArray | null;
  while ((match = regex.exec(content)) !== null) {
    count++;
    // 防止死循环：如果正则表达式不包含全局标志 g，则每次 exec 都会返回相同的结果
    if (!regex.global) {
      break;
    }
  }
  return count;
};

// 直接替换 %1 %2 ... %9 的占位符为对应的参数值
const generatePhraseToContent = (
  phraseContent: string,
  params: string[]
): string => {
  return phraseContent.replace(/(^|[^\\])%(\d)/g, (match, prefix, numStr) => {
    const index = parseInt(numStr, 10) - 1;
    const replacement = params[index] || `%${numStr}`;
    return prefix + replacement;
  });
};

const addPhrase = async (phrase: Phrase): Promise<AxiosResp> => {
  console.log("添加短语", phrase);
  return api.request
    .quickPost("/addphrase", {
      phrase,
    })
    .then((resp: AxiosResp) => {
      phraseList.value.push(phrase as Phrase);
      console.log("phrase.ts 添加短语成功", phraseList);
      return resp;
    })
    .catch((err: AxiosError) => {
      const resp = err.response as AxiosResp;
      window.$notifyError("添加短语失败", resp.msg);
      console.error("添加短语失败", resp.msg);
      return Promise.reject(resp);
    });
};

// 将同名 phrase 删除
const removePhrase = async (
  type: "static" | "dynamic",
  phraseName: string
): Promise<AxiosResp> => {
  const index = phraseList.value.findIndex(
    (p: Phrase) => p.name === phraseName
  );
  if (index === -1) {
    console.error("短语不存在");
    return Promise.reject({ msg: "短语不存在" });
  }
  return api.request
    .quickPost("/deletephrase", {
      name: phraseName,
    })
    .then((resp: AxiosResp) => {
      phraseList.value = phraseList.value.filter(
        (p: Phrase) => p.name !== phraseName
      );
      return resp;
    })
    .catch((err: AxiosError) => {
      const resp = err.response as AxiosResp;
      window.$notifyError("删除短语失败", resp.msg);
      console.error("删除短语失败", resp.msg);
      return Promise.reject(resp);
    });
};

export {
  phraseList,
  dynamicPhrase,
  phraseTypes,
  phraseInit,
  generatePhraseContent,
  processMdContent,
  phraseContentToPhraseWithParams,
  countParams,
  generatePhraseToContent,
  addPhrase,
  removePhrase,
};
